import { useEffect, useRef, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Plus, Trash2, Bot, User as UserIcon, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SUGGESTED = [
  "How many overdue invoices do we have?",
  "Which business unit made the most revenue?",
  "List my top 3 leads by value.",
  "How many high-priority tickets are open?",
  "What's our profit margin this month?",
];

export default function AIAssistant() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const loadSessions = () => api.get("/ai/chat/sessions").then(r => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);

  const openSession = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/ai/chat/sessions/${id}`);
    setMessages(data.messages || []);
  };

  const newChat = () => { setActiveId(null); setMessages([]); };

  const send = async (msg) => {
    const text_to_send = (msg || text).trim();
    if (!text_to_send || busy) return;
    setBusy(true);
    // Optimistic user message
    const optimisticId = `tmp-${Date.now()}`;
    setMessages(prev => [...prev, { id: optimisticId, role: "user", text: text_to_send, at: new Date().toISOString() }]);
    setText("");
    try {
      const { data } = await api.post("/ai/chat", { message: text_to_send, session_id: activeId });
      setActiveId(data.session_id);
      setMessages(prev => [...prev.filter(m => m.id !== optimisticId),
        { id: `u-${Date.now()}`, role: "user", text: text_to_send, at: new Date().toISOString() },
        data.message,
      ]);
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI request failed");
      setMessages(prev => prev.filter(m => m.id !== optimisticId));
    } finally { setBusy(false); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/ai/chat/sessions/${id}`);
    if (activeId === id) newChat();
    loadSessions();
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  return (
    <div className="space-y-6 h-full flex flex-col">
      <PageHeader
        title="AI Assistant"
        subtitle="Ask natural-language questions about your CRM. Grounded in live Figuraid data."
        actions={<Button onClick={newChat} variant="outline" data-testid="new-chat-btn"><Plus className="w-4 h-4 mr-1" /> New chat</Button>}
      />

      <div className="flex-1 flex gap-4 min-h-[600px]">
        {/* Sessions sidebar */}
        <aside className="w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100 dark:border-slate-800">Recent chats</div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.map(s => (
              <div key={s.id} className={`group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer ${activeId === s.id ? "bg-brand-aqua-light text-brand-aqua-hover" : "hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"}`}
                   onClick={() => openSession(s.id)} data-testid={`ai-session-${s.id}`}>
                <MessageSquare className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                <span className="flex-1 truncate">{s.title}</span>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {sessions.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No chats yet</div>}
          </div>
        </aside>

        {/* Main chat pane */}
        <div className="flex-1 flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-aqua to-brand-navy flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-lg text-slate-900 dark:text-slate-100">Hi {user?.name?.split(" ")[0]} 👋</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">Ask me anything about your CRM data.</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SUGGESTED.map(s => (
                    <button key={s} onClick={() => send(s)} data-testid="suggested-prompt"
                            className="text-left px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-brand-aqua hover:bg-brand-aqua-light/40 text-sm text-slate-700 dark:text-slate-300 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map(m => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-aqua to-brand-navy flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-brand-aqua text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200"}`}>
                  {m.text}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-700 flex items-center justify-center shrink-0">
                    <UserIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-aqua to-brand-navy flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-2.5 flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-slate-200 dark:border-slate-800 p-3 flex gap-2">
            <Input placeholder="Ask about leads, invoices, tickets, revenue..."
                   value={text} onChange={e => setText(e.target.value)}
                   onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                   disabled={busy} data-testid="ai-input" />
            <Button onClick={() => send()} disabled={busy || !text.trim()}
                    className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="ai-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
